import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, Leaf, Video, MapPin, CheckCircle2, Clock, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const CONSULT_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": ["MedicalBusiness", "HealthAndBeautyBusiness"],
    "name": "IIY Phytoventra Ltd — Medical Herbalist Consultations",
    "alternateName": "Invest In Yourself",
    "url": "https://iiypv.com/medical-herbalist-consultation",
    "telephone": "+44-1604-279001",
    "email": "sales@iiypv.com",
    "priceRange": "££",
    "image": "https://iiypv.com/og-image.jpg",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Northampton",
      "addressCountry": "GB"
    },
    "areaServed": ["Northampton", "Northamptonshire", "United Kingdom"],
    "availableService": [
      { "@type": "Medicaltherapy", "name": "Free 15-Minute Discovery Call", "description": "A quick assessment of personal health goals, supplement compatibility and botanical guidance with a registered medical herbalist." },
      { "@type": "MedicalTherapy", "name": "Comprehensive 1-to-1 Herbal Consultation", "description": "In-depth evaluation of diet, lifestyle and prescription medication, with a personalised botanical plan." }
    ],
    "employee": {
      "@type": "Person",
      "name": "Varsha Soneji",
      "jobTitle": "Medical Herbalist",
      "description": "Medical Herbalist with 37+ years of clinical herbal experience."
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "Can I book a consultation with IIY (Invest In Yourself)?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. IIY Phytoventra offers a free 15-minute discovery call and a comprehensive 1-to-1 consultation with registered Medical Herbalist Mrs. Varsha Soneji, available in person in Northampton and online across the UK." } },
      { "@type": "Question", "name": "What services does Invest In Yourself offer in Northampton?", "acceptedAnswer": { "@type": "Answer", "text": "In-person and online medical herbalist consultations, natural wellness and lifestyle guidance, and distribution of clinical-grade turmeric/curcumin (BCM-95®) food supplements." } },
      { "@type": "Question", "name": "How can I improve my health with IIY (Invest In Yourself)?", "acceptedAnswer": { "@type": "Answer", "text": "Start with a free discovery call to discuss your wellbeing goals with our medical herbalist, who can suggest tailored botanical guidance and lifestyle steps alongside our BCM-95® range. This is general wellbeing support and not a substitute for medical advice." } }
    ]
  }
];

export default function MedicalHerbalistConsultation() {
  return (
    <div className="min-h-screen bg-stone-50" data-testid="consultation-page">
      <SEO
        title="Book a Medical Herbalist Consultation in Northampton & Online | IIY Phytoventra"
        description="Book a consultation with registered Medical Herbalist Mrs. Varsha Soneji (37+ years). Free 15-min discovery call or full 1-to-1 herbal consultation — in Northampton or online across the UK."
        keywords="book medical herbalist consultation Northampton, medical herbalist online UK, Varsha Soneji herbalist, free discovery call herbalist, herbal consultation Northampton"
        jsonLd={CONSULT_JSONLD}
      />

      <section className="bg-gradient-to-br from-emerald-800 to-emerald-950 text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-emerald-200 font-semibold mb-3 uppercase tracking-wide text-sm">Consultation · Book a Herbalist</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-5 font-heading" data-testid="consultation-h1">Book a Medical Herbalist Consultation in Northampton &amp; Online</h1>
          <p className="text-lg text-emerald-100/90 mb-8 max-w-2xl">Personalised botanical guidance with registered Medical Herbalist Mrs. Varsha Soneji. Available in person in Northampton and remotely via video across the UK.</p>
          <Link to="/discovery-call" data-testid="consultation-book-cta" className="inline-flex items-center gap-2 bg-amber-400 text-emerald-950 font-bold px-7 py-3.5 rounded-full hover:bg-amber-300 transition-colors">
            <CalendarCheck className="w-5 h-5" /> Book your free discovery call <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-14 space-y-14">
        <section>
          <h2 className="text-2xl font-bold text-emerald-900 mb-4 font-heading">Meet your herbalist — Mrs. Varsha Soneji</h2>
          <div className="bg-white rounded-2xl border border-border p-6 md:p-8 flex gap-5 items-start">
            <div className="w-14 h-14 rounded-xl bg-emerald-700 text-white flex items-center justify-center flex-shrink-0"><Leaf className="w-7 h-7" /></div>
            <div>
              <p className="text-gray-700 leading-relaxed">Mrs. Varsha Soneji is a <strong>Medical Herbalist with over 37 years of clinical herbal experience</strong>. She provides friendly, no-pressure guidance on natural wellbeing, everyday mobility and how clinically studied botanicals such as BCM-95® curcumin can fit into a balanced lifestyle. Consultations include a review of your goals, diet and lifestyle, and any current supplements or prescription medication for safety.</p>
              <p className="text-sm text-gray-500 mt-3">Professional herbal practice · UK-registered medical herbalist.</p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-emerald-900 mb-6 font-heading">Consultation options</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="consultation-option-discovery">
              <div className="flex items-center gap-2 text-emerald-700 mb-2"><Clock className="w-5 h-5" /><span className="font-bold">Free 15-Minute Discovery Call</span></div>
              <p className="text-gray-700 text-sm leading-relaxed">A quick assessment of your personal health goals, supplement compatibility and botanical guidance. Perfect if you're new and want a friendly starting point.</p>
              <p className="mt-3 text-emerald-800 font-semibold">Free</p>
            </div>
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="consultation-option-full">
              <div className="flex items-center gap-2 text-emerald-700 mb-2"><CheckCircle2 className="w-5 h-5" /><span className="font-bold">Comprehensive 1-to-1 Herbal Consultation</span></div>
              <p className="text-gray-700 text-sm leading-relaxed">An in-depth evaluation covering diet, lifestyle and a review of prescription medication, with a personalised botanical plan tailored to you.</p>
              <p className="mt-3 text-emerald-800 font-semibold">By arrangement</p>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-gray-600">
            <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-emerald-700" /> In person in Northampton</span>
            <span className="inline-flex items-center gap-2"><Video className="w-4 h-4 text-emerald-700" /> Online via video across the UK</span>
          </div>
        </section>

        <section className="bg-emerald-50 rounded-2xl border border-emerald-100 p-6 md:p-8 text-center">
          <h2 className="text-2xl font-bold text-emerald-900 mb-3 font-heading">Pick a time that suits you</h2>
          <p className="text-gray-700 mb-6 max-w-xl mx-auto">Choose a slot for your free discovery call and we'll confirm by email with your video link or call details.</p>
          <Link to="/discovery-call" data-testid="consultation-book-cta-2" className="inline-flex items-center gap-2 bg-emerald-700 text-white font-bold px-7 py-3.5 rounded-full hover:bg-emerald-800 transition-colors">
            <CalendarCheck className="w-5 h-5" /> Book my consultation
          </Link>
          <p className="text-xs text-gray-500 mt-4">Food supplements are not a substitute for a varied, balanced diet, a healthy lifestyle, or professional medical care.</p>
        </section>
      </div>
    </div>
  );
}
